import React from 'react';
import { FiClock, FiDollarSign, FiTrendingUp, FiTrendingDown } from 'react-icons/fi';
import '../../styles/Dashboard.css';

const fmtS = n => `S/ ${parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDt = ts => ts ? new Date(ts).toLocaleString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Lima' }) : '—';

function elapsed(openedAt, closedAt) {
  if (!openedAt) return '—';
  const end = closedAt ? new Date(closedAt) : new Date();
  const ms = end - new Date(openedAt);
  if (ms < 0) return '—';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m`;
}

export default function CajeroDashboard({ data }) {
  const { register, summary } = data;

  if (!register) {
    return (
      <div className="dsh-container">
        <div className="dsh-empty-state">
          <FiClock size={32} />
          <h2>Aún no has abierto una caja</h2>
          <p>Cuando abras tu caja, aquí verás las ventas, cobros y movimientos de tu turno.</p>
        </div>
      </div>
    );
  }

  const { ventas = [], ventas_total = 0, cobros = [], cobros_total = 0, movimientos = {}, diferencia = 0 } = summary || {};
  const ventasConMonto = ventas.filter(v => v.total > 0);
  const cobrosConMonto = cobros.filter(c => c.total > 0);

  return (
    <div className="dsh-container">
      <div className="dsh-header">
        <div>
          <h1 className="dsh-title">Mi Caja</h1>
          <p className="dsh-subtitle">{register.caja_name} · {register.warehouse_name}</p>
        </div>
        <span className={`dsh-caja-badge ${register.status === 'open' ? 'dsh-caja-open' : 'dsh-caja-closed'}`}>
          {register.status === 'open' ? 'CAJA ABIERTA' : 'CAJA CERRADA'}
        </span>
      </div>

      <div className="dsh-kpi-grid dsh-kpi-grid-4">
        <div className="dsh-kpi-card" style={{ background: '#eff6ff', borderColor: '#bfdbfe' }}>
          <div className="dsh-kpi-icon" style={{ color: '#3b82f6' }}><FiTrendingUp /></div>
          <div>
            <div className="dsh-kpi-value">{fmtS(ventas_total)}</div>
            <div className="dsh-kpi-label">Ventas del turno</div>
          </div>
        </div>
        <div className="dsh-kpi-card" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
          <div className="dsh-kpi-icon" style={{ color: '#16a34a' }}><FiDollarSign /></div>
          <div>
            <div className="dsh-kpi-value">{fmtS(cobros_total)}</div>
            <div className="dsh-kpi-label">Cobros de crédito</div>
          </div>
        </div>
        <div className="dsh-kpi-card" style={{ background: '#fff7ed', borderColor: '#fed7aa' }}>
          <div className="dsh-kpi-icon" style={{ color: '#d97706' }}><FiClock /></div>
          <div>
            <div className="dsh-kpi-value">{elapsed(register.opened_at, register.closed_at)}</div>
            <div className="dsh-kpi-label">Abierta desde {fmtDt(register.opened_at)}</div>
          </div>
        </div>
        <div className="dsh-kpi-card" style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}>
          <div className="dsh-kpi-icon" style={{ color: '#475569' }}>
            {parseFloat(diferencia) < 0 ? <FiTrendingDown /> : <FiTrendingUp />}
          </div>
          <div>
            <div className="dsh-kpi-value">{fmtS(movimientos.total_efectivo)}</div>
            <div className="dsh-kpi-label">Total efectivo esperado</div>
          </div>
        </div>
      </div>

      <section className="dsh-section">
        <div className="dsh-section-title"><span className="dsh-dot" /> VENTAS POR FORMA DE PAGO</div>
        <div className="dsh-payment-grid">
          {ventasConMonto.length === 0 ? (
            <div className="dsh-empty">Sin ventas registradas en este turno</div>
          ) : ventasConMonto.map(v => (
            <div key={v.name} className="dsh-payment-card">
              <span className="dsh-payment-name">{v.name}</span>
              <span className="dsh-payment-amount">{fmtS(v.total)}</span>
            </div>
          ))}
        </div>
      </section>

      {cobrosConMonto.length > 0 && (
        <section className="dsh-section">
          <div className="dsh-section-title"><span className="dsh-dot" /> COBROS DE CRÉDITO REGISTRADOS</div>
          <div className="dsh-payment-grid">
            {cobrosConMonto.map(c => (
              <div key={c.name} className="dsh-payment-card">
                <span className="dsh-payment-name">{c.name}</span>
                <span className="dsh-payment-amount">{fmtS(c.total)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="dsh-section">
        <div className="dsh-section-title"><span className="dsh-dot" /> MOVIMIENTOS EN CAJA</div>
        <div className="dsh-cash-rows">
          <div className="dsh-cash-row"><span>Monto Apertura</span><span>{fmtS(movimientos.open_amount)}</span></div>
          <div className="dsh-cash-row dsh-cash-pos"><span>Ventas en Efectivo</span><span>+{fmtS(movimientos.ventas_efectivo)}</span></div>
          <div className="dsh-cash-row dsh-cash-pos"><span>Ingreso Efectivo</span><span>+{fmtS(movimientos.ingreso_efectivo)}</span></div>
          <div className="dsh-cash-row dsh-cash-neg"><span>Salida Efectivo</span><span>-{fmtS(movimientos.salida_efectivo)}</span></div>
          <div className="dsh-cash-row dsh-cash-total"><span>TOTAL EFECTIVO</span><span>{fmtS(movimientos.total_efectivo)}</span></div>
        </div>
      </section>
    </div>
  );
}
